import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function VancouverEateriesHiddenGems() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "vancouver-hidden-gems-eateries";

  // 2) Access articles from your context
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =====================================
  // ========== SECTION TITLES IDS =======
  // =====================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Caffé La Tana (Italian Café & Grocer)",
    "section-2": "Aleph Eatery (Vegan Middle Eastern)",
    "section-3": "Maenam (Upscale Thai)",
    "section-4": "Sashimiya (Japanese Sushi Shop)",
    "section-5": "Grapes & Soda (Natural Wine Bar)",
    "section-6": "Cartems Donuts (Artisanal Donut Shop)",
    "section-7": "Vij’s (Innovative Indian)",
    "section-8": "The Mackenzie Room (Farm-to-Table)",
    "section-9": "Yasma (Syrian & Lebanese)",
    "section-10": "The Acorn (Vegetarian Fine Dining)",
    faq: "FAQ",
    "local-regulations": "Local Regulations & Best Practices",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism Board",
    },
    {
      url: "https://tryhiddengems.com/",
      label: "Hidden Gems Vancouver",
    },
    {
      text: "Vancouver Tourism Neighbourhood Map (various official PDFs)",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.2765,
      lng: -123.0701,
      popupText: "Caffé La Tana – Italian Café & Grocer",
    },
    {
      id: "section-2",
      lat: 49.2836,
      lng: -123.0663,
      popupText: "Aleph Eatery – Vegan Middle Eastern",
    },
    {
      id: "section-3",
      lat: 49.2683,
      lng: -123.1479,
      popupText: "Maenam – Upscale Thai Dining",
    },
    {
      id: "section-4",
      lat: 49.2755,
      lng: -123.1290,
      popupText: "Sashimiya – Japanese Sushi Shop",
    },
    {
      id: "section-5",
      lat: 49.2673,
      lng: -123.1400,
      popupText: "Grapes & Soda – Natural Wine Bar & Bistro",
    },
    {
      id: "section-6",
      lat: 49.2663,
      lng: -123.0989,
      popupText: "Cartems Donuts – Artisanal Donut Shop",
    },
    {
      id: "section-7",
      lat: 49.2585,
      lng: -123.1140,
      popupText: "Vij’s – Innovative Indian Cuisine",
    },
    {
      id: "section-8",
      lat: 49.2835,
      lng: -123.0964,
      popupText: "The Mackenzie Room – Farm-to-Table",
    },
    {
      id: "section-9",
      lat: 49.2935,
      lng: -123.1327,
      popupText: "Yasma – Syrian & Lebanese Flavours",
    },
    {
      id: "section-10",
      lat: 49.2490,
      lng: -123.0987,
      popupText: "The Acorn – Vegetarian Fine Dining",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Vancouver’s Hidden Gems: Underrated Eateries to Explore</title>
        <meta
          name="description"
          content="A thorough guide to Vancouver’s underrated restaurants, complete with coordinates, best dishes, local regulations, and practical tips."
        />
        <meta
          name="keywords"
          content="Vancouver, hidden gems, eateries, restaurants, local dining, travel tips"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/vancouver-hidden-gems-eateries.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Vancouver’s Hidden Gems: Underrated Eateries to Explore"
        />
        <meta
          property="og:description"
          content="An in-depth look at lesser-known but amazing Vancouver restaurants, their specialties, and practical tips for visiting."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/vancouver-hidden-gems-eateries.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/vancouver-hidden-gems-eateries.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Vancouver’s Hidden Gems: Underrated Eateries to Explore"
        />
        <meta
          name="twitter:description"
          content="Discover Vancouver’s best-kept foodie secrets."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/vancouver-hidden-gems-eateries.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left labelsMap={labelsMap} locations={locations} mapRef={mapRef} spotRefs={spotRefs} />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="An array of hidden gem eateries in Vancouver"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver is a food lover’s paradise beyond the well-trodden
              tourist spots. This guide uncovers hidden gem eateries—from cozy
              cafes to family-run restaurants and artisan bakeries—that locals
              adore. We’ve included a variety of cuisines, from local West Coast
              fare to international flavors. Whether you’re on a budget or
              seeking an upscale experience, there’s something here for you.
              Below are some lesser-known but highly beloved spots, complete
              with practical tips, coordinates, and must-try menu items.
            </p>

            {/* SECTION 1: Caffé La Tana */}
            <h2 id="section-1">Caffé La Tana (Italian Café & Grocer)</h2>
            <p ref={(el) => setSpotRef("section-1", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2765}
                coordX={-123.0701}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              635 Commercial Drive, Vancouver, BC V5L 3W3
            </p>
            <p>
              <strong>Hours:</strong> Daily 11:00 AM – 10:00 PM<br />
              <strong>Approx. Cost:</strong> $$ (~$15–$30 per person)<br />
              <strong>Highlights:</strong> An intimate Italian
              <em> alimentari</em>-style café serving fresh pasta, wine, and
              scratch-made pastries. With just 20 seats, it offers a cozy vibe
              and top-notch homemade pasta like rigatoni in spicy vodka sauce.
            </p>

            {/* SECTION 2: Aleph Eatery */}
            <h2 id="section-2">Aleph Eatery (Vegan Middle Eastern)</h2>
            <p ref={(el) => setSpotRef("section-2", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2836}
                coordX={-123.0663}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1889 Powell Street, Vancouver, BC V5L 1H8
            </p>
            <p>
              <strong>Hours:</strong> Wed–Sun 11:00 AM – 9:00 PM<br />
              <strong>Approx. Cost:</strong> $$ (~$15–$25 per person)<br />
              <strong>Highlights:</strong> 100% plant-based Middle Eastern
              dishes like Falafel Benny and Silk Road hummus. A bright, casual
              spot offering modern twists on Levantine flavors. Limited seating,
              so consider takeout during busy hours.
            </p>

            {/* SECTION 3: Maenam */}
            <h2 id="section-3">Maenam (Upscale Thai)</h2>
            <p ref={(el) => setSpotRef("section-3", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2683}
                coordX={-123.1479}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1938 West 4th Avenue, Vancouver, BC V6J 1M5
            </p>
            <p>
              <strong>Hours:</strong> Lunch Wed–Sat, Dinner Daily<br />
              <strong>Approx. Cost:</strong> $$$ (tasting menu ~$88)<br />
              <strong>Highlights:</strong> Award-winning Thai cuisine with a
              West Coast twist. Known for its Khao Man Tod (crispy rice salad)
              and refined tasting menus. Stylish but unpretentious, perfect for
              date nights or foodie explorations.
            </p>

            {/* SECTION 4: Sashimiya */}
            <h2 id="section-4">Sashimiya (Japanese Sushi Shop)</h2>
            <p ref={(el) => setSpotRef("section-4", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2755}
                coordX={-123.1290}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1348 Hornby Street, Vancouver, BC V6Z 1W5
            </p>
            <p>
              <strong>Hours:</strong> Tue–Sat 11:00 AM – 6:00 PM<br />
              <strong>Approx. Cost:</strong> $$ (~$10–$20 for sushi combos)<br />
              <strong>Highlights:</strong> A small Japanese grocery with
              restaurant-quality sushi. Affordable pre-made sashimi and nigiri,
              plus a few seats for quick bites. High fish-to-rice ratio and
              incredibly fresh seafood make it a real hidden gem.
            </p>

            {/* SECTION 5: Grapes & Soda */}
            <h2 id="section-5">Grapes & Soda (Natural Wine Bar & Bistro)</h2>
            <p ref={(el) => setSpotRef("section-5", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2673}
                coordX={-123.1400}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              1541 West 6th Avenue, Vancouver, BC V6J 1R1
            </p>
            <p>
              <strong>Hours:</strong> Tue–Sat 5:00 PM – 11:00 PM<br />
              <strong>Approx. Cost:</strong> $$ (plates ~$10–$20)<br />
              <strong>Highlights:</strong> Vancouver’s first natural wine bar.
              Rustic-chic interior with a rotating menu of small plates made
              from fresh, local produce. Perfect for late-night bites or a cozy
              date night. Limited seating—arrive early or expect a wait.
            </p>

            {/* SECTION 6: Cartems Donuts */}
            <h2 id="section-6">Cartems Donuts (Artisanal Donut Shop)</h2>
            <p ref={(el) => setSpotRef("section-6", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2663}
                coordX={-123.0989}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              2190 Main Street, Vancouver, BC V5T 3C5
            </p>
            <p>
              <strong>Hours:</strong> Daily 8:00 AM – 6:00 PM<br />
              <strong>Approx. Cost:</strong> $ (donuts ~$3–$4)<br />
              <strong>Highlights:</strong> Gourmet donut pioneer. Offers unique
              flavors like Earl Grey, Citrus Glazed, and seasonal specialties.
              Vegan and gluten-free options available. Ideal for a sweet treat
              paired with locally roasted coffee.
            </p>

            {/* SECTION 7: Vij’s */}
            <h2 id="section-7">Vij’s (Innovative Indian)</h2>
            <p ref={(el) => setSpotRef("section-7", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2585}
                coordX={-123.1140}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              3106 Cambie Street, Vancouver, BC V5Z 2W2
            </p>
            <p>
              <strong>Hours:</strong> Daily 5:30 PM – 10:00 PM<br />
              <strong>Approx. Cost:</strong> $$$ (entrées ~$20–$30)<br />
              <strong>Highlights:</strong> A landmark for modern Indian cuisine
              by celebrity chef Vikram Vij. Famous for Lamb Popsicles and unique
              regional curries. No reservations for small groups, so arrive
              early to avoid long waits.
            </p>

            {/* SECTION 8: The Mackenzie Room */}
            <h2 id="section-8">The Mackenzie Room (Farm-to-Table)</h2>
            <p ref={(el) => setSpotRef("section-8", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2835}
                coordX={-123.0964}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              415 Powell Street, Vancouver, BC V6A 1G7
            </p>
            <p>
              <strong>Hours:</strong> Daily 5:30 PM – 10:00 PM<br />
              <strong>Approx. Cost:</strong> $$$ (5-course ~$75)<br />
              <strong>Highlights:</strong> Seasonal tasting menus in a
              rustic-chic setting. Known for zero-waste cooking and creative
              dishes like “Chicken of the Sea” (uni bread) or adventurous cuts
              of meat. The menu changes often, keeping each visit unique.
            </p>

            {/* SECTION 9: Yasma */}
            <h2 id="section-9">Yasma (Syrian & Lebanese Flavours)</h2>
            <p ref={(el) => setSpotRef("section-9", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2935}
                coordX={-123.1327}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              550 Denman Street, Vancouver, BC V6G 3H1
            </p>
            <p>
              <strong>Hours:</strong> Wed–Sun 4:45 PM – 9:45 PM<br />
              <strong>Approx. Cost:</strong> $$ (mezzes ~$8–$12, mains $18–$30)<br />
              <strong>Highlights:</strong> Authentic dishes like Fatteh Hummus
              and Shish Tawouk, served in a modern setting. Tucked near the
              seawall, it’s an upscale but lesser-known gem for Middle Eastern
              cuisine.
            </p>

            {/* SECTION 10: The Acorn */}
            <h2 id="section-10">The Acorn (Vegetarian Fine Dining)</h2>
            <p ref={(el) => setSpotRef("section-10", el)} style={{ scrollMarginTop: "80px" }}>
              <strong>Location:</strong>{" "}
              <Marker
                coordY={49.2490}
                coordX={-123.0987}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              3995 Main Street, Vancouver, BC V5V 3P3
            </p>
            <p>
              <strong>Hours:</strong> Dinner nightly; Weekend brunch<br />
              <strong>Approx. Cost:</strong> $$$ (tasting menus ~$45–$79)<br />
              <strong>Highlights:</strong> A local award-winning
              vegetarian/vegan powerhouse focusing on sustainability and
              artistic plating. Dishes rotate seasonally, showcasing everything
              from wild foraged mushrooms to zero-waste pasta sauces.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do I need reservations at these hidden gems?</h3>
              <p>
                Most spots here are small and popular with locals, so
                reservations are recommended for dinner service. If
                reservations aren’t available, arriving when doors open or
                visiting at off-peak times can help secure a table.
              </p>

              <h3>2. Are there vegan or vegetarian options?</h3>
              <p>
                Yes. Many of these eateries have vegan/vegetarian dishes. Aleph
                is fully plant-based, and The Acorn is vegetarian fine dining.
                Cartems offers vegan donuts, and most others have at least one
                vegetarian or vegan dish.
              </p>

              <h3>3. How’s public transit to these locations?</h3>
              <p>
                Vancouver’s transit is reliable. SkyTrain and buses cover these
                neighborhoods. For places farther from a station (like The
                Mackenzie Room in Railtown), consider a short rideshare or taxi
                if traveling at night.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              <strong>Tipping:</strong> It’s customary to tip 15–20% at
              restaurants. Counter-service eateries may have tip jars, but it’s
              not mandatory.
            </p>
            <p>
              <strong>Eco-conscious Packaging:</strong> Vancouver bans
              single-use foam containers and plastic straws in most cases.
              You’ll find recyclable or compostable materials for takeout. Help
              by sorting waste into the correct bins.
            </p>
            <p>
              <strong>Reservations & Family Friendliness:</strong> Some venues
              are small (like Grapes & Soda), so consider leaving kids at home,
              especially for late-night visits. For day cafés or donuts (Cartems,
              Sashimiya), families are generally welcome.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Vancouver’s dining scene is overflowing with hidden treasures. By
              exploring these lesser-known gems, you’ll taste world-class
              cuisine without the usual crowds. Whether you want artisanal
              donuts, innovative Indian, or sustainable farm-to-table dishes,
              each spot offers a unique perspective on Vancouver’s diverse food
              culture. Grab a friend—or go solo—and start your gastronomic
              adventure today!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay ref={mobileMapRef} markers={locations} spotRefs={spotRefs} />

      <Footer />
    </>
  );
}